import React, { useState, useEffect, useRef } from "react";
import Icon from "../../components/ui/icon";
import SideNav from "../../components/layout/sideNav";
import { useToast } from "../../context/toastContext";
import { useNavigate } from "react-router-dom";
import api from "../../services/apiClient";
import "../../assets/styles/pages/publishers/cashbackoffers.css";
import DOMPurify from "dompurify";
import defaultImage from "../../assets/images/DefaultImage.png";

const CashbackOffers = () => {
  const navigate = useNavigate();
  const showToast = useToast();
  const [isSideNavActive, setisSideNavActive] = useState(false);
  const [offers, setOffers] = useState([]);
  const [userInfo, setUserInfo] = useState(
    JSON.parse(localStorage.getItem("PublisherInfo")) || {}
  );
  const [CBOFStatus, setCBOFStatus] = useState("fetching_first");
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [userVerified, setUserVerified] = useState(false);
  const [showSearchBar, setShowSearchBar] = useState(true);
  const isLoadingMore = useRef(false);
  const limit = 20;
  const [updatingID, setUpdatingID] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("aToken");
    if (!token) {
      navigate("/auth/login");
      return;
    }

    const validateUser = async () => {
      if (userVerified) return;

      api
        .get(`/api/publisher/info`)
        .then((response) => {
          setUserInfo(response.data.data);
          setUserVerified(true);
          localStorage.setItem(
            "PublisherInfo",
            JSON.stringify(response.data.data)
          );

          if (response?.data?.data?.cboAllowed) {
            fetchoffer();
          } else {
            setCBOFStatus("notAllowed");
            setShowSearchBar(false);
          }
        })
        .catch((err) => {
          showToast({
            status: "error",
            message: err.response?.data?.message || "Login not found.",
          });
          navigate("/auth/login");
        });
    };

    const fetchoffer = () => {
      if (CBOFStatus === "fetching" || !hasMore) return;
      if (CBOFStatus !== "fetching_first") setCBOFStatus("fetching");

      api
        .get(`/api/cashback-offer?limit=${limit}&skip=${skip}`)
        .then((response) => {
          if (response.data.status) {
            const fetchedoffer = response.data.offer;

            if (fetchedoffer.length < limit) {
              setHasMore(false);
              setCBOFStatus("not_needed");
            }

            isLoadingMore.current = false;

            setOffers((prev) => [...prev, ...fetchedoffer]);
            setSkip((prevSkip) => prevSkip + limit);
            setCBOFStatus("success");
          } else {
            setCBOFStatus("failed");
            if (CBOFStatus === "fetching_first") setShowSearchBar(false);
          }
          if (CBOFStatus === "fetching_first") {
            if (response?.data?.offer.length < 0) {
              setShowSearchBar(false);
            }
          }
        })
        .catch((err) => {
          console.error(err);
          setCBOFStatus("failed");
          if (CBOFStatus === "fetching_first") setShowSearchBar(false);
        });
    };

    const handleScroll = () => {
      if (
        window.innerHeight + window.scrollY >=
        document.body.offsetHeight - 200 &&
        hasMore
      ) {
        if (CBOFStatus === "fetching_first") return;
        if (CBOFStatus === "notAllowed") return;
        if (CBOFStatus === "not_needed") return;
        if (isLoadingMore.current) return;
        isLoadingMore.current = true;
        fetchoffer();
      }
    };

    validateUser();

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [CBOFStatus, hasMore, navigate, showToast, skip, userVerified]);

  const updateOfferStatus = (oID) => {
    if (updatingID) return;

    const offerID = DOMPurify.sanitize(oID);
    setUpdatingID(offerID);
    setOffers((prevOffers) =>
      prevOffers.map((offer) =>
        offer.offerID === offerID ? { ...offer, status: "updating" } : offer
      )
    );

    api
      .put("/api/cashback-offer/update-status", { offerID })
      .then((response) => {
        if (response.data.status) {
          setOffers((prevOffers) =>
            prevOffers.map((offer) =>
              offer.offerID === offerID
                ? { ...offer, status: response.data.newStatus }
                : offer
            )
          );
        } else {
          showToast({
            status: "error",
            message: response?.data?.message || "Something went wrong.",
          });
        }
        setUpdatingID(null);
      })
      .catch((err) => {
        showToast({
          status: "error",
          message: err.response?.data?.message || "Something went wrong.",
        });
        setUpdatingID(null);
      });
  };

  return (
    <div className="cboffers__page">
      <SideNav
        userName={userInfo.name}
        emailID={userInfo.email}
        active={isSideNavActive}
        campaignOpenInital={false}
        accManagerName={userInfo.adminInfo?.name}
        accManagerEmail={userInfo.adminInfo?.email}
      />
      <div
        onClick={() => setisSideNavActive(!isSideNavActive)}
        className={
          isSideNavActive ? "background-shadow active" : "background-shadow"
        }
      ></div>
      <main>
        <header className="desktop-header">
          <div>
            <h2>Cashback Offers</h2>
            <p>Share cashback links for your users.</p>
          </div>
          <button onClick={() => navigate("/cashback-offers/create")}>
            Create <Icon name="PlusCircle" className="onRight" />
          </button>
        </header>

        <header className="mobile-header">
          <div>
            <h2>Cashback Offers</h2>
          </div>
          <button
            className="nav-btn"
            onClick={() => setisSideNavActive(!isSideNavActive)}
          >
            {isSideNavActive ? (
              <Icon name="XMark" className="onLeft" />
            ) : (
              <Icon name="Bars3" className="onLeft" />
            )}
          </button>
        </header>

        {showSearchBar && (
          <div className="search-box">
            <Icon name="MagnifyingGlass" />
            <input
              type="text"
              placeholder="Search for offer by name eg: Swift Digital Ads"
            />
          </div>
        )}

        <div className="offers-container">
          {offers && offers.length > 0 ? (
            offers.map((offer, index) => {
              return (
                <div className="offer-box" key={offer.campaignId}>
                  <div className="top">
                    <div className="offer-info">
                      <img src={defaultImage} alt="offer" />
                      <div>
                        <span>{offer.campaignName}</span>
                        <span>No Conversion yet.</span>
                      </div>
                    </div>
                    <button
                      className={`${offer.status === "Live" ? "pause" : ""}`}
                      onClick={() => updateOfferStatus(offer.offerID)}
                      disabled={updatingID === offer.offerID}
                    >
                      {offer.status === "Live" ? (
                        <Icon name="Pause" />
                      ) : offer.status === "Paused" ? (
                        <Icon name="Play" />
                      ) : offer.status === "updating" ? (
                        <Icon name="ArrowPath" className="spin" />
                      ) : (
                        ""
                      )}
                    </button>
                  </div>
                  <div className="payout-info">
                    <div>
                      <span>User Profit</span>
                      <span>₹{offer.goals[0].cashbackAmount}</span>
                    </div>
                    <div>
                      <span>Refer Profit</span>
                      <span>₹{offer.goals[0].referAmount}</span>
                    </div>
                    <div>
                      <span>Your Profit</span>
                      <span>₹{offer.goals[0].publisherProfit}</span>
                    </div>
                  </div>
                  <button className="main-btn">View More</button>
                </div>
              );
            })
          ) : CBOFStatus !== "fetching_first" &&
            CBOFStatus !== "fetching" &&
            CBOFStatus !== "failed" &&
            CBOFStatus !== "notAllowed" ? (
            <div className="no-offer">
              <Icon className="main" name="InformationCircle" />
              <h3>No Cashback Offer Found!</h3>
              <p>Seems like you have not created any offer till now.</p>
            </div>
          ) : (
            ""
          )}
          {CBOFStatus === "failed" && (
            <div className="no-offer">
              <Icon className="main" name="FaceFrown" />
              <h3>Something went wrong!</h3>
              <p>
                Soory, but we are unable to fetch your cashback offers right
                now.
              </p>
              <button>
                <Icon name="ArrowPath" /> Retry
              </button>
            </div>
          )}
          {!userInfo?.cboAllowed && CBOFStatus === "notAllowed" && (
            <div className="no-offer">
              <Icon className="main" name="FaceFrown" />
              <h3>This account is not eligible to use this feature!</h3>
              <p>Only approved ones can use.</p>
            </div>
          )}
          {CBOFStatus === "fetching_first" && (
            <>
              <div className="offer-box loading"></div>
              <div className="offer-box loading"></div>
              <div className="offer-box loading"></div>
            </>
          )}
        </div>
        {showSearchBar && (
          <span className="copyright-text">
            © 2025 Swift Digital Ads, All rights reserved
          </span>
        )}
      </main>
    </div>
  );
};

export default CashbackOffers;
